import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Save, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";

const LABELS = {
  company_profile: "Company Profile",
  user_roles_permissions: "User Roles & Permissions",
  energy_tariff: "Energy Tariff Settings",
  diesel_rate: "Diesel Rate Settings",
  quality_specs: "Quality Specification Limits",
  maintenance_categories: "Maintenance Categories",
  breakdown_categories: "Breakdown Categories",
  equipment_categories: "Equipment Categories",
  vendor_categories: "Vendor Categories",
  spare_categories: "Spare Categories",
  notification_settings: "Notification Settings",
  approval_matrix: "Approval Matrix",
  email_templates: "Email Templates",
  report_templates: "Report Templates",
  dashboard_preferences: "Dashboard Preferences",
  project_settings: "Project Settings",
};

export default function Settings() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const readOnly = user?.role !== "super_admin";

  useEffect(() => {
    api.get("/settings/categories").then((r) => {
      setCats(r.data);
      const sec = params.get("section");
      if (sec && r.data.includes(sec)) setActive(sec);
      else if (r.data.length) setActive(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/settings/${active}`).then((r) => {
      setData(r.data); setText(JSON.stringify(r.data.value, null, 2));
    });
  }, [active]);

  const save = async () => {
    let val;
    try { val = JSON.parse(text); } catch (e) { return toast.error("Invalid JSON: " + e.message); }
    setSaving(true);
    try { await api.put(`/settings/${active}`, { value: val }); toast.success("Saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <AppLayout title="System Administration" subtitle="Master data, settings and configuration · Editable by Super Admin">
      {readOnly && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm" data-testid="settings-readonly-banner">
          <ShieldAlert className="h-4 w-4" /> Read-only view. Only Super Admin can save changes.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        <Card className="p-2 self-start" data-testid="settings-sidebar">
          <div className="label-cap text-slate-500 px-3 pt-2 pb-1">Categories</div>
          <div className="space-y-0.5">
            {cats.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`settings-tab-${c}`}
                className={`w-full text-left px-3 py-2 rounded text-sm ${active === c ? "bg-sky-50 text-sky-800 font-medium" : "hover:bg-slate-50 text-slate-700"}`}>
                {LABELS[c] || c}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap text-slate-500">Master setting</div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: "Chivo" }}>{LABELS[active] || active}</h3>
              {data?.is_default && <div className="text-xs text-slate-400 mt-0.5">Default values shown — not yet customised.</div>}
              {data?.updated_at && <div className="text-xs text-slate-400 mt-0.5">Last updated by {data.updated_by} at {data.updated_at?.slice(0, 19).replace("T", " ")}</div>}
            </div>
            <Button onClick={save} disabled={saving || readOnly} className="bg-sky-900 hover:bg-sky-950" data-testid="settings-save-button">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <Textarea
            value={text} onChange={(e) => setText(e.target.value)}
            disabled={readOnly} data-testid="settings-json-editor"
            className="font-mono text-xs min-h-[480px]"
            spellCheck={false}
          />
          <div className="mt-2 text-xs text-slate-500">
            Edit JSON directly. The schema is module-specific (lists for *_categories, nested objects for templates / specs / matrix). Changes are audit-logged with previous & new values.
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
